import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  Renderer2,
} from "@angular/core";
import LoggerService from "@bfx/logger";
import { ATDLDataModel } from "app/order-entry/data-atdl/model/atdl-data.model";
import { ATDLParameterDataModel } from "app/order-entry/data-atdl/model/atdl-parameter-data.model";
import { OEInputModel } from "app/order-entry/data/models/oe-input.model";
import { ICAutocompleteGenericModel } from "app/order-entry/interface/controls/models/bfx-interface-controls.models";
import { OEAlgoParamsComponent } from "app/order-entry/interface/oe-panel-algo-params.component";
import { OEAdapterForwardService } from "app/order-entry/services/adapter/oe-adapter-forward.service";
import { OEAdapterReverseService } from "app/order-entry/services/adapter/oe-adapter-reverse.service";
import { ATDLDatasetService } from "app/order-entry/services/atdl/atdl-dataset.service";
import { ATDLUtilityService } from "app/order-entry/services/atdl/atdl-utility.service";
import { ATDLValidatorService } from "app/order-entry/services/atdl/atdl-validator.service";
import { OELoggerService } from "app/order-entry/services/basic/oe-logger.service";
import { OELookupService } from "app/order-entry/services/basic/oe-lookup.service";
import { OEUtilityService } from "app/order-entry/services/basic/oe-utility.service";
import { OEApiService } from "app/order-entry/services/oe-api.service";
import { OECoordinatorService } from "app/order-entry/services/oe-coordinator.service";
import { StrategyParameterImpl } from "shared/cubiq-model-multi-asset";
const logger = LoggerService.for(__dirname);

/**
 * Type used in onChange events
 */
export type ParameterChange = {
  parameters: StrategyParameterImpl[];
  errors: OEInputModel[];
};

/**
 * Subcomponent of normal ATDL Params component that is initialised from an external ATDLDataModel instead of an algoType
 * which is looked up in the system.
 * It emits change events (of type ParameterChange) that can be monitored to ensure parameter values are updated
 */
@Component({
  selector: "atdl-designer-params",
  templateUrl: "../order-entry/interface/html/oe-panel-algo-params.html",
  standalone: false,
})
export class BfxATDLDesignerParamsComponent extends OEAlgoParamsComponent {
  @Output() onChange: EventEmitter<ParameterChange> = new EventEmitter();

  constructor(
    oeLogger: OELoggerService,
    coordinator: OECoordinatorService,
    api: OEApiService,
    reverseAdapter: OEAdapterReverseService,
    forwardAdapter: OEAdapterForwardService,
    utilityService: OEUtilityService,
    lookup: OELookupService,
    renderer2: Renderer2,
    cd: ChangeDetectorRef,
    atdlDataset: ATDLDatasetService,
    atdlValidator: ATDLValidatorService,
    atdlUtility: ATDLUtilityService,
    el: ElementRef,
  ) {
    super(
      oeLogger,
      coordinator,
      api,
      reverseAdapter,
      forwardAdapter,
      utilityService,
      lookup,
      renderer2,
      cd,
      atdlDataset,
      atdlValidator,
      atdlUtility,
      el,
    );
  }

  /**
   * suplied with an ATDL Model instead of an algoType to remove requirement to look up the
   * specified type in the system
   */
  @Input("atdlModel")
  public set atdlModel(value: ATDLDataModel) {
    if (value) {
      this.algoType = new ICAutocompleteGenericModel(value.name, value.name);
      this.dataModel = value;

      this.isInitializing = this.strategyParameters == null;
      this.isInitializingEmbedded = this.strategyParameters != null;

      this.algoId = this.algoType.value;

      this.reInitialize();

      setTimeout(
        () => {
          this.createAlgoControlsStructure();
          this.createLocalDataModel();
          setTimeout(() => {
            // If this is not in a timeout, the the params will all have inital value of null and not be included
            // in designer grid
            this.emitParameterModel();
          }, 1000);
        },
        this.strategyParameters != null ? 0 : 100,
      );

      this.outputExtendedLogging(
        `ALGO: PANEL -> Changed to model for ${this.algoType.value} dump:`,
        this.dataModel,
      );
    } else {
      this.dataModel = null;
      this.algoId = null;
      this.cleanupAllInterfaceControls();
      logger.out.info(`INFO: oeAlgoParamsPanel.ngOnChanges('algoType'): Model set to NULL!`);
    }
  }

  /**
   *  Override to dispatch change events
   * @param parameterName
   * @param parameterValue
   */
  public override updateAlgoParameterValue(parameterName: string, parameterValue: any): void {
    super.updateAlgoParameterValue(parameterName, parameterValue);
    this.emitParameterModel();
  }

  /**
   * Emit a change event with errors and active parameters
   */
  private emitParameterModel(): void {
    if (this.entryModel) {
      const errors: OEInputModel[] = [];
      const parameters = this.forwardAdapter.createAlgoStrategyParameters(this.entryModel, errors);
      this.onChange.emit({ errors, parameters });
    }
  }

  /******************************/
  // API Interface methods than should be overridden if component is used without an OE Data Model

  /**
   * Push defined strategy parameters into the OE Data Model
   */
  protected override updateAlgoParametersInApi() {
    // For designer, we no not have a top-level order model
  }

  /**
   * Is the top-level order part-filled?
   * @returns
   */
  protected entryModelPartFilled(): boolean {
    // WE always mark algo as unfilled in designer
    return false;
  }

  /**
   * Push any parameter update out to the top-level order data model
   * @param parameter
   */
  protected override updateAlgoParameterInApi(parameter: ATDLParameterDataModel) {
    // For designer, we no not have a top-level order model
    this.updateAlgoParameterValue(parameter.refName, parameter.value);
  }
}
